import { useLingui } from '@lingui/react/macro'
import CantileverSymbol from 'assets/cantilever-symbol.svg?react'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import { useSidebar } from 'designSystem/components/Sidebar'
import { Text } from 'designSystem/components/Text'
import { useIsMobile } from 'designSystem/hooks/useMobile'
import { useNavbarData } from 'features/navbar/useNavbarData'
import { Menu } from 'lucide-react'
import type { ReactNode } from 'react'
import styled from 'styled-components'

interface EmbeddedSidebarMobileHeaderFrameProps {
  children: ReactNode
  actions?: ReactNode
}

export function EmbeddedSidebarMobileHeaderFrame({
  children,
  actions,
}: EmbeddedSidebarMobileHeaderFrameProps) {
  const { t } = useLingui()
  const { toggleSidebar } = useSidebar()

  return (
    <Header>
      <Button
        type='button'
        variant='outline'
        size='icon-lg'
        aria-label={t`Open navigation menu`}
        title={t`Open navigation menu`}
        onClick={toggleSidebar}>
        <Menu />
      </Button>
      {children}
      <Box display='flex' align='center' justify='end'>
        {actions}
      </Box>
    </Header>
  )
}

interface EmbeddedSidebarMobileHeaderProps {
  orgOrSiteID: string | undefined
}

export function EmbeddedSidebarMobileHeader(
  props: EmbeddedSidebarMobileHeaderProps
) {
  const isMobile = useIsMobile()
  if (!isMobile) return null
  return <SiteTitleHeader {...props} />
}

function SiteTitleHeader({ orgOrSiteID }: EmbeddedSidebarMobileHeaderProps) {
  const { site, organization } = useNavbarData(orgOrSiteID)
  // eslint-disable-next-line lingui/no-unlocalized-strings -- Product name fallback.
  const title = site?.data.name ?? organization?.data.name ?? 'Cantilever'

  return (
    <EmbeddedSidebarMobileHeaderFrame>
      <Title>
        <CantileverSymbol aria-hidden='true' />
        <Text as='span' variant='caption' weight='semibold' truncate>
          {title}
        </Text>
      </Title>
    </EmbeddedSidebarMobileHeaderFrame>
  )
}

const MENU_BUTTON_SIZE = '2rem'

const Header = styled.header`
  z-index: 0;
  display: grid;
  grid-template-columns: ${MENU_BUTTON_SIZE} minmax(0, 1fr) ${MENU_BUTTON_SIZE};
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  min-height: 2.75rem;
  padding: 0.35rem 0.5rem;
  background: var(--sidebar-accent);
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
`

const Title = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  color: var(--foreground);

  > svg {
    width: 1rem;
    height: auto;
    flex-shrink: 0;
  }

  > svg path {
    fill: currentColor;
    fill-opacity: 1;
  }
`
